import React, { createContext, FC, useContext, useEffect, useState } from 'react';
import { ticketState } from '../../../../enums';
import { IQueue, IServiceStatistic, ITicket } from '../../model';
import { compareByVisitTime, comparePriorityAndTime, filterTicketByDropdown, filterTicketsByTerm } from '../helpers/Comparers';
import useLocationServices from '../hooks/useLocationServices';
import { IFilterSettings } from '../Queue';

const TicketsContext = createContext<{
    allTickets: ITicket[];
    waitingTickets: ITicket[];
    stoppedTickets: ITicket[];
    removedTickets: ITicket[];
    waitingTicketsSettings: IFilterSettings;
    setWaitingTicketsSettings: Function;
    stoppedTicketsSettings: IFilterSettings;
    setStoppedTicketsSettings: Function;
    removedTicketsSettings: IFilterSettings;
    setRemovedTicketsSettings: Function;
}>({
    allTickets: [],
    waitingTickets: [],
    stoppedTickets: [],
    removedTickets: [],
    waitingTicketsSettings: null!,
    setWaitingTicketsSettings: null!,
    stoppedTicketsSettings: null!,
    setStoppedTicketsSettings: null!,
    removedTicketsSettings: null!,
    setRemovedTicketsSettings: null!
});

const TicketsContextProvider: FC<{ queues: IQueue[]; services: IServiceStatistic[]; debouncedSearchTerm: string }> = ({
    queues,
    services,
    debouncedSearchTerm,
    children
}) => {
    const [allTickets, setAllTickets] = useState<ITicket[]>([]);

    const [waitingTickets, setWaitingTickets] = useState<ITicket[]>([]);
    const [waitingTicketsSettings, setWaitingTicketsSettings] = useState<IFilterSettings>({
        serviceFilterOptionKeys: [],
        choiceGroupValue: 1
    });

    const [stoppedTickets, setStoppedTickets] = useState<ITicket[]>([]);
    const [stoppedTicketsSettings, setStoppedTicketsSettings] = useState<IFilterSettings>({
        serviceFilterOptionKeys: [],
        choiceGroupValue: 1
    });

    const [removedTickets, setRemovedTickets] = useState<ITicket[]>([]);
    const [removedTicketsSettings, setRemovedTicketsSettings] = useState<IFilterSettings>({
        serviceFilterOptionKeys: [],
        choiceGroupValue: 1
    });

    const ticketFilterOptions = useLocationServices(services, waitingTicketsSettings.serviceFilterOptionKeys);

    useEffect(() => {
        let currTickets: ITicket[] = [];
        queues.forEach(
            (x) => x.isSelectedInLocation && (currTickets = currTickets.concat(x.tickets.filter((y) => !currTickets.some((c) => c.id === y.id))))
        );

        currTickets.sort(comparePriorityAndTime);

        if (debouncedSearchTerm !== '') {
            currTickets = currTickets.filter((x) => filterTicketsByTerm(x, debouncedSearchTerm));
        }

        setAllTickets(currTickets);

        waitingTickets.length ? localStorage.setItem('nextTicketId', waitingTickets[0].id.toString()) : localStorage.setItem('nextTicketId', '');
    }, [debouncedSearchTerm, queues]);

    useEffect(() => {
        setWaitingTickets(
            allTickets
                .filter((x) => x.state === ticketState.Waiting)
                .filter((x) => filterTicketByDropdown(x, waitingTicketsSettings.serviceFilterOptionKeys))
                .sort(waitingTicketsSettings.choiceGroupValue === 1 ? comparePriorityAndTime : compareByVisitTime)
        );
    }, [waitingTicketsSettings, allTickets]);

    useEffect(() => {
        setStoppedTickets(
            allTickets
                .filter((x) => x.state === ticketState.Stopped)
                .filter((x) => filterTicketByDropdown(x, stoppedTicketsSettings.serviceFilterOptionKeys))
                .sort(stoppedTicketsSettings.choiceGroupValue === 1 ? comparePriorityAndTime : compareByVisitTime)
        );
    }, [stoppedTicketsSettings, allTickets]);

    useEffect(() => {
        setRemovedTickets(
            allTickets
                .filter((x) => x.state === ticketState.Removed)
                .filter((x) => filterTicketByDropdown(x, removedTicketsSettings.serviceFilterOptionKeys))
                .sort(removedTicketsSettings.choiceGroupValue === 1 ? comparePriorityAndTime : compareByVisitTime)
        );
    }, [removedTicketsSettings, allTickets]);

    useEffect(() => {
        if (localStorage.getItem('automatedCall') == 'true' && localStorage.getItem('nextTicketId')!.length && waitingTickets.length > 1) {
            localStorage.setItem('nextTicketId', waitingTickets[0].id.toString());
        }
    }, [waitingTickets]);

    useEffect(() => {
        setWaitingTicketsSettings({ ...waitingTicketsSettings, serviceFilterOptionKeys: ticketFilterOptions });
        setStoppedTicketsSettings({ ...stoppedTicketsSettings, serviceFilterOptionKeys: ticketFilterOptions });
        setRemovedTicketsSettings({ ...removedTicketsSettings, serviceFilterOptionKeys: ticketFilterOptions });
    }, [ticketFilterOptions]);

    return (
        <TicketsContext.Provider
            value={{
                allTickets,
                waitingTickets,
                stoppedTickets,
                removedTickets,
                waitingTicketsSettings,
                setWaitingTicketsSettings,
                stoppedTicketsSettings,
                setStoppedTicketsSettings,
                removedTicketsSettings,
                setRemovedTicketsSettings
            }}
        >
            {children}
        </TicketsContext.Provider>
    );
};

const useTickets = () => useContext(TicketsContext);

export { TicketsContextProvider, useTickets };
